import { useTerminology } from '@/lib/useTerminology'
/**
 * AP-13: Edit/New-Seite für TimeProjectRef.
 *
 * Routen:
 *   /time-tracking/projects/new
 *   /time-tracking/projects/:id/edit
 *
 * AP-36 Phase E: Im Bearbeiten-Modus speichert jedes Feld beim Verlassen — ohne
 * Knopf. Der Anlegen-Modus bleibt ein Formular mit lokalem Zustand und dem Knopf
 * „Anlegen".
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/axios';
import {
  FeldStatus,
  feldKlasse,
  mitStand,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  createProjectRef,
  deleteProjectRef,
  getProjectRef,
  getRefSchemas,
} from '../api/time-master-data.api';
import { CustomFieldsForm, validateRequired } from '../components/CustomFieldsForm';
import { ModeBanner } from '../components/ModeBanner';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import {
  fieldLabel,
  fieldWrapper,
  formCard,
  inputBase,
  pageTitle,
  selectBase,
  textareaBase,
} from '@/lib/form-styles';
import type { CustomFieldsSchema, TimeProjectRef } from '../types/time-master-data.types';
import { bestaetige } from '@/lib/dialoge';

type Status = TimeProjectRef['status'];
const STATUSES: Status[] = ['PLANNED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'];

interface FormState {
  projectNumber: string;
  name: string;
  description: string;
  status: Status;
  isActive: boolean;
  customFields: Record<string, unknown>;
}

const EMPTY: FormState = {
  projectNumber: '',
  name: '',
  description: '',
  status: 'ACTIVE',
  isActive: true,
  customFields: {},
};

const ladeText = <div className="p-6 text-sm text-muted-foreground">Wird geladen …</div>;

/** Pflichtfelder der Zusatzfelder — dieselbe Prüfung wie früher vor dem Speichern. */
function pflichtfelderText(schema: CustomFieldsSchema | null, werte: Record<string, unknown>): string | null {
  const fehlend = Object.keys(validateRequired(schema, werte));
  return fehlend.length === 0 ? null : `Pflichtfeld fehlt: ${fehlend.join(', ')}`;
}

export default function TimeProjectRefFormPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <BearbeitenSeite id={id} /> : <AnlegenSeite />;
}

// ── Bearbeiten: feldweise, ohne Knopf ───────────────────────────────────────

function BearbeitenSeite({ id }: { id: string }) {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('project');

  const [row, setRow] = useState<TimeProjectRef | null>(null);
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [schemas, geladen] = await Promise.all([getRefSchemas(), getProjectRef(id)]);
        if (cancelled) return;
        setSchema(schemas.project);
        setRow(geladen);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `timeProjectRef:${id}`,
    bezeichnung: row ? `${terms.project} ${row.projectNumber}` : terms.project,
    daten: row ? (row as unknown as Datensatz) : null,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/time-master-data/projects/${id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-master-data/projects/${id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  const gesperrt = !canEdit;
  const nummer       = useFeld<string | null>(stelle, 'projectNumber', { label: terms.number, pflicht: true, gesperrt });
  const status       = useFeld<string | null>(stelle, 'status', { label: 'Status', art: 'auswahl', gesperrt });
  const bezeichnung  = useFeld<string | null>(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt });
  const beschreibung = useFeld<string | null>(stelle, 'description', { label: 'Beschreibung', gesperrt });
  const aktiv        = useFeld<boolean>(stelle, 'isActive', { label: 'aktiv', art: 'schalter', gesperrt });

  // Die Zusatzfelder sind ein Eintrag: bearbeitet wird ein Entwurf, gespeichert
  // wird er beim Verlassen der Hülle (der Abschnitt darunter).
  const daten = useStellenDaten(stelle);
  const gespeicherteZusatzfelder = useMemo(
    () => (daten.customFields as Record<string, unknown> | null) ?? {},
    [daten.customFields],
  );
  const zusatzfelder = useEntwurf<Record<string, unknown>>(stelle, {
    kennung: 'customFields',
    gespeichert: gespeicherteZusatzfelder,
    felder: ['customFields'],
    label: 'Zusatzfelder',
    gesperrt,
    operation: (entwurf) => () => ({ customFields: entwurf }),
    pruefe: (entwurf) => pflichtfelderText(schema, entwurf),
  });

  const deaktivieren = async () => {
    if (!(await bestaetige(`${terms.project} wirklich deaktivieren? ${terms.project === 'Auftrag' ? 'Er' : 'Es'} bleibt erhalten und kann später wieder aktiviert werden.`))) return;
    setError(null);
    try { await deleteProjectRef(id); navigate('/time-tracking/projects'); }
    catch (e) { setError((e as Error).message); }
  };

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>{terms.project} bearbeiten</h1>
        <button type="button" onClick={() => navigate('/time-tracking/projects')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="project" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>{terms.number} *</label>
            <input type="text" className={feldKlasse} {...nummer.props} />
            <FeldStatus status={nummer} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Status</label>
            <select className={feldKlasse} {...status.props}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <FeldStatus status={status} />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Bezeichnung *</label>
          <input type="text" className={feldKlasse} {...bezeichnung.props} />
          <FeldStatus status={bezeichnung} />
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Beschreibung</label>
          <textarea className={feldKlasse} rows={3} {...beschreibung.props} />
          <FeldStatus status={beschreibung} />
        </div>

        <div>
          <label className="flex items-center gap-2 select-none">
            <input type="checkbox" className="h-4 w-4" {...aktiv.schalter} />
            <span className="text-sm">aktiv</span>
          </label>
          <FeldStatus status={aktiv} />
        </div>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'} {...zusatzfelder.huelle}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={zusatzfelder.entwurf ?? {}}
            onChange={(v) => zusatzfelder.setze(v)}
            disabled={gesperrt} />
          <FeldStatus status={zusatzfelder} />
        </section>
      )}

      <div>
        <button type="button" onClick={deaktivieren} disabled={gesperrt}
          className="px-3 py-2 text-sm rounded-md border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-50">
          Deaktivieren
        </button>
      </div>
    </div>
  );
}

// ── Anlegen: Formular mit Knopf ─────────────────────────────────────────────

function AnlegenSeite() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('project');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cfErrors, setCfErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    getRefSchemas()
      .then((schemas) => { if (!cancelled) setSchema(schemas.project); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
    };
  }, []);

  const disabled = !canEdit || saving;

  const submit = async () => {
    setError(null);
    if (!form.projectNumber.trim()) { setError(`${terms.number} ist Pflicht.`); return; }
    if (!form.name.trim())          { setError('Bezeichnung ist Pflicht.');   return; }
    const reqErrors = validateRequired(schema, form.customFields);
    if (Object.keys(reqErrors).length > 0) {
      setCfErrors(reqErrors);
      setError('Bitte Pflichtfelder ausfüllen.');
      return;
    }
    setCfErrors({});
    setSaving(true);
    try {
      await createProjectRef({
        projectNumber: form.projectNumber.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        status: form.status,
        isActive: form.isActive,
        customFields: form.customFields,
      });
      navigate('/time-tracking/projects');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(
    () => `${terms.mode === 'ORDER' ? 'Neuer' : 'Neues'} ${terms.project}`,
    [terms],
  );

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>{title}</h1>
        <button type="button" onClick={() => navigate('/time-tracking/projects')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="project" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>{terms.number} *</label>
            <input type="text" className={inputBase}
              value={form.projectNumber}
              onChange={(e) => setForm((f) => ({ ...f, projectNumber: e.target.value }))}
              disabled={disabled} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Status</label>
            <select className={selectBase}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Status }))}
              disabled={disabled}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Bezeichnung *</label>
          <input type="text" className={inputBase}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={disabled} />
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Beschreibung</label>
          <textarea className={textareaBase} rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            disabled={disabled} />
        </div>

        <label className="flex items-center gap-2 select-none">
          <input type="checkbox" checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            disabled={disabled} className="h-4 w-4" />
          <span className="text-sm">aktiv</span>
        </label>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={form.customFields}
            onChange={(v) => setForm((f) => ({ ...f, customFields: v }))}
            disabled={disabled} />
          {Object.keys(cfErrors).length > 0 && (
            <ul className="text-xs text-red-600 list-disc list-inside">
              {Object.entries(cfErrors).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
            </ul>
          )}
        </section>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => navigate('/time-tracking/projects')}
          className="px-3 py-2 text-sm rounded-md border" disabled={saving}>
          Abbrechen
        </button>
        <button type="button" onClick={submit} disabled={disabled}
          className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {saving ? 'Legt an …' : 'Anlegen'}
        </button>
      </div>
    </div>
  );
}
